import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import SignatureCapture from "@/components/SignatureCapture";
import { humanizeAxiosError } from "@/lib/api";
import { Upload, Download, Star, Eye, EyeSlash, Sparkle } from "@phosphor-icons/react";
import ReactMarkdown from "react-markdown";

const API = process.env.REACT_APP_BACKEND_URL;

export default function Payroll() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [upload, setUpload] = useState({ user_id: "", period: "", file: null });
  const [uploadOpen, setUploadOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkUpload, setBulkUpload] = useState({ period: "", file: null });
  const [bulkResult, setBulkResult] = useState(() => {
    try {
      const saved = sessionStorage.getItem("payroll_bulk_result");
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });
  const [uploading, setUploading] = useState(false);
  const [signOpen, setSignOpen] = useState(null);
  const [rejectOpen, setRejectOpen] = useState(null);
  const [sig, setSig] = useState("");
  const [reason, setReason] = useState("");
  const [feedbackOpen, setFeedbackOpen] = useState(null);
  const [rating, setRating] = useState(0);
  const [ratingComment, setRatingComment] = useState("");
  const [forceUploadConfirm, setForceUploadConfirm] = useState(null); // 409 mismatch dialog state
  const [interpretOpen, setInterpretOpen] = useState(null); // AI yorum dialog state
  const [interpretText, setInterpretText] = useState("");
  const [interpretLoading, setInterpretLoading] = useState(false);
  const isAdmin = ["admin", "hr", "gm"].includes(user?.role);

  const load = useCallback(() => {
    api.get("/payroll").then((r) => setItems(r.data));
    if (isAdmin) api.get("/personnel").then((r) => setUsers(r.data));
  }, [isAdmin]);
  useEffect(() => { load(); }, [load]);

  // Bildirimden gelen ?focus=<payroll_id> — ilgili bordro satırını vurgular ve kaydırır.
  const [focusId, setFocusId] = useState(null);
  useEffect(() => {
    const fid = new URLSearchParams(window.location.search).get("focus");
    if (!fid || !items.length) return;
    if (items.some((p) => p.id === fid)) {
      setFocusId(fid);
      window.history.replaceState({}, "", window.location.pathname);
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="pay-row-${fid}"]`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 200);
      setTimeout(() => setFocusId(null), 4000);
    }
  }, [items]);

  const doUpload = async (force = false) => {
    if (!upload.user_id || !upload.period || !upload.file) { toast.error("Alanları doldurun"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("user_id", upload.user_id);
      fd.append("period", upload.period);
      fd.append("file", upload.file);
      if (force) fd.append("force", "true");
      await api.post("/payroll/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Bordro yüklendi ve personele bildirildi");
      setUpload({ user_id: "", period: "", file: null });
      setUploadOpen(false);
      setForceUploadConfirm(null);
      load();
    } catch (e) {
      // 409 = content/person mismatch → ask admin to confirm "yine de yükle"
      if (e?.response?.status === 409) {
        setForceUploadConfirm({ detail: e.response.data?.detail || "İçerik eşleşmedi" });
      } else {
        toast.error(humanizeAxiosError(e));
      }
    } finally {
      setUploading(false);
    }
  };

  const [bulkPreview, setBulkPreview] = useState(null);
  const [bulkPreviewing, setBulkPreviewing] = useState(false);

  const doBulkPreview = async () => {
    if (!bulkUpload.file) return toast.error("Önce PDF dosyası seçin");
    setBulkPreviewing(true);
    try {
      const fd = new FormData(); fd.append("file", bulkUpload.file);
      const { data } = await api.post("/payroll/bulk-preview", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 300000 });
      setBulkPreview(data);
      toast.success(`Önizleme: ${data.matched.length}/${data.total_pages} eşleşti`);
    } catch (e) { toast.error(humanizeAxiosError(e)); }
    finally { setBulkPreviewing(false); }
  };

  const doBulkUpload = async () => {
    if (!bulkUpload.period || !bulkUpload.file) return toast.error("Alanları doldurun");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("period", bulkUpload.period);
      fd.append("file", bulkUpload.file);
      const { data } = await api.post("/payroll/bulk-upload", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 600000 });
      setBulkResult(data);
      try { sessionStorage.setItem("payroll_bulk_result", JSON.stringify(data)); } catch {}
      toast.success(`${data.matched.length} personele ait bordro yüklendi`);
      setBulkOpen(false);
      setBulkUpload({ period: "", file: null });
      setBulkPreview(null);
      load();
    } catch (e) {
      toast.error(humanizeAxiosError(e));
    } finally {
      setUploading(false);
    }
  };

  const doSign = async () => {
    const chosen = sig || user?.signature;
    if (!chosen) return toast.error("Önce Ayarlar sayfasından imzanızı yükleyin");
    const signed = signOpen;
    await api.post(`/payroll/${signed.id}/sign`, { signature: chosen });
    toast.success("Onaylandı");
    setSignOpen(null); setSig(""); load();
    // Ask for satisfaction feedback right after successful sign.
    setFeedbackOpen(signed);
    setRating(0); setRatingComment("");
  };

  const submitFeedback = async () => {
    if (!feedbackOpen || !rating) { setFeedbackOpen(null); return; }
    try {
      await api.post(`/payroll/${feedbackOpen.id}/feedback`, { rating, comment: ratingComment });
      toast.success("Geri bildiriminiz için teşekkürler");
    } catch (e) {
      toast.error(humanizeAxiosError(e));
    }
    setFeedbackOpen(null); setRating(0); setRatingComment("");
  };
  const skipFeedback = () => { setFeedbackOpen(null); setRating(0); setRatingComment(""); };
  const doReject = async () => {
    if (!reason.trim()) return toast.error("Açıklama gerekli");
    await api.post(`/payroll/${rejectOpen.id}/reject`, { reason });
    toast.success("Reddedildi"); setRejectOpen(null); setReason(""); load();
  };

  const doInterpret = async (p) => {
    setInterpretOpen(p);
    setInterpretText("");
    setInterpretLoading(true);
    try {
      const r = await api.post(`/payroll/${p.id}/interpret`);
      setInterpretText(r.data.interpretation || "");
    } catch (e) {
      toast.error(humanizeAxiosError(e));
      setInterpretOpen(null);
    } finally {
      setInterpretLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Bordro</div>
          <h1 className="font-display text-3xl font-bold mt-1">Ücret Hesap Pusulaları</h1>
        </div>
        {isAdmin && (
          <div className="flex gap-2 flex-wrap">
            <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
              <DialogTrigger asChild><Button variant="outline" data-testid="bulk-upload-btn"><Upload size={16} className="mr-1" />Toplu PDF Yükle</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Toplu Bordro Yükleme (Otomatik Eşleşme)</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div className="text-xs text-muted-foreground bg-secondary p-3 rounded">
                    Tek bir PDF yükleyin. Her sayfada bir personelin bordrosu olmalı. Sistem her sayfayı okur ve TC kimlik veya ad-soyad eşleşmesine göre otomatik olarak ilgili personele iletir.
                  </div>
                  <div><Label>Dönem (YYYY-MM)</Label><Input placeholder="2026-02" value={bulkUpload.period} onChange={(e) => setBulkUpload({...bulkUpload, period: e.target.value})} data-testid="bulk-period" /></div>
                  <div><Label>PDF Dosyası</Label><Input type="file" accept="application/pdf" onChange={(e) => { setBulkUpload({...bulkUpload, file: e.target.files?.[0]}); setBulkPreview(null); }} data-testid="bulk-file" /></div>
                  {bulkPreview && (
                    <div className="rounded-md border border-primary/30 bg-primary/5 p-3 space-y-2 text-xs" data-testid="bulk-preview">
                      <div className="flex items-center justify-between font-semibold">
                        <span>Önizleme: {bulkPreview.total_pages} sayfa</span>
                        <span className="text-emerald-700 dark:text-emerald-400">{bulkPreview.matched.length} eşleşti</span>
                      </div>
                      {bulkPreview.unmatched_pages?.length > 0 && (
                        <div className="text-destructive">⚠ {bulkPreview.unmatched_pages.length} sayfa eşleşmedi: sayfa {bulkPreview.unmatched_pages.join(", ")}</div>
                      )}
                      <div className="max-h-32 overflow-y-auto space-y-0.5">
                        {bulkPreview.matched.slice(0, 20).map((m) => (
                          <div key={`${m.user_id}-${m.page}`}>✓ Sayfa {m.page} → {m.name}</div>
                        ))}
                        {bulkPreview.matched.length > 20 && <div className="text-muted-foreground">…ve {bulkPreview.matched.length - 20} daha</div>}
                      </div>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={doBulkPreview} disabled={bulkPreviewing || !bulkUpload.file} data-testid="bulk-preview-btn">
                      {bulkPreviewing ? "Analiz ediliyor…" : "Önizle (Eşleşme Kontrolü)"}
                    </Button>
                    <Button onClick={doBulkUpload} disabled={uploading || !bulkUpload.file || !bulkUpload.period} data-testid="bulk-submit">
                      {uploading ? "Yükleniyor…" : "Yükle ve Eşleştir"}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
              <DialogTrigger asChild><Button data-testid="upload-payroll-btn"><Upload size={16} className="mr-1" />Tekli Yükle</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Ücret Pusulası Yükle</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Personel</Label>
                  <Select value={upload.user_id} onValueChange={(v) => setUpload({...upload, user_id: v})}>
                    <SelectTrigger data-testid="pay-user"><SelectValue placeholder="Seçin" /></SelectTrigger>
                    <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Dönem (YYYY-MM)</Label><Input placeholder="2026-02" value={upload.period} onChange={(e) => setUpload({...upload, period: e.target.value})} data-testid="pay-period" /></div>
                <div><Label>PDF Dosyası</Label><Input type="file" accept="application/pdf" onChange={(e) => setUpload({...upload, file: e.target.files?.[0]})} data-testid="pay-file" /></div>
                <Button onClick={() => doUpload(false)} disabled={uploading} data-testid="pay-upload-submit">{uploading ? "Yükleniyor…" : "Yükle ve Gönder"}</Button>
              </div>
            </DialogContent>
          </Dialog>
          </div>
        )}
      </div>

      {isAdmin && bulkResult && (
        <Card data-testid="bulk-result-card" className="border-primary/40 bg-primary/5">
          <CardContent className="p-6 space-y-3">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Toplu Yükleme Raporu</div>
                <div className="font-display text-lg font-bold mt-0.5">
                  {bulkResult.period} · {bulkResult.total_pages} sayfa · <span className="text-emerald-700 dark:text-emerald-400">{bulkResult.matched.length} eşleşti</span>
                  {bulkResult.unmatched_pages?.length > 0 && (
                    <span> · <span className="text-destructive">{bulkResult.unmatched_pages.length} eşleşmedi</span></span>
                  )}
                </div>
                {bulkResult.at && (
                  <div className="text-[11px] text-muted-foreground mt-1">
                    Yükleme zamanı: {new Date(bulkResult.at).toLocaleString("tr-TR")}
                  </div>
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setBulkResult(null); sessionStorage.removeItem("payroll_bulk_result"); }}
                data-testid="bulk-result-clear"
              >
                Raporu Kapat
              </Button>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <div className="text-xs font-semibold uppercase text-muted-foreground mb-2">✓ Eşleşen ({bulkResult.matched.length})</div>
                <div className="rounded-md border border-border bg-background max-h-56 overflow-y-auto">
                  {bulkResult.matched.length === 0 ? (
                    <div className="p-3 text-xs text-muted-foreground">Hiçbir sayfa eşleşmedi.</div>
                  ) : (
                    <ul className="divide-y divide-border text-sm">
                      {bulkResult.matched.map((m) => (
                        <li key={`${m.user_id}-${m.page}`} className="px-3 py-1.5 flex items-center justify-between gap-2">
                          <span className="font-medium">{m.name}</span>
                          <Badge variant="outline" className="text-[10px]">Sayfa {m.page}</Badge>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              <div>
                <div className="text-xs font-semibold uppercase text-muted-foreground mb-2">✗ Eşleşmeyen ({bulkResult.unmatched_pages?.length || 0})</div>
                <div className="rounded-md border border-destructive/30 bg-background max-h-56 overflow-y-auto">
                  {!bulkResult.unmatched_pages?.length ? (
                    <div className="p-3 text-xs text-emerald-700 dark:text-emerald-400">Tüm sayfalar eşleşti 🎉</div>
                  ) : (
                    <ul className="divide-y divide-border text-xs">
                      {(bulkResult.unmatched_details || bulkResult.unmatched_pages.map((p) => ({ page: p, snippet: "" }))).map((d) => (
                        <li key={d.page} className="px-3 py-2">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="destructive" className="text-[10px]">Sayfa {d.page}</Badge>
                            <span className="text-muted-foreground">TC/ad-soyad tespit edilemedi</span>
                          </div>
                          {d.snippet && (
                            <div className="text-[11px] text-muted-foreground bg-muted/50 rounded p-1.5 font-mono truncate" title={d.snippet}>
                              {d.snippet}
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                {bulkResult.unmatched_pages?.length > 0 && (
                  <div className="text-[11px] text-muted-foreground mt-2">
                    💡 Eşleşmeyen sayfaları "Tekli Yükle" ile manuel olarak yükleyebilirsiniz.
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-muted-foreground text-xs uppercase tracking-wider bg-muted/50">
                <tr>
                  <th className="text-left p-3">Personel</th><th className="text-left p-3">Dönem</th><th className="text-left p-3">Yüklenme</th>
                  <th className="text-left p-3">Son Onay</th><th className="text-left p-3">Durum</th><th className="text-left p-3">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {items.map((p) => (
                  <tr key={p.id} data-testid={`pay-row-${p.id}`} className={`border-t border-border transition-colors ${focusId === p.id ? "bg-primary/10 ring-2 ring-primary/40" : ""}`}>
                    <td className="p-3">{p.user_name}</td>
                    <td className="p-3">{p.period}</td>
                    <td className="p-3">{p.uploaded_at?.slice(0,10)}</td>
                    <td className="p-3">{p.deadline?.slice(0,10)}</td>
                    <td className="p-3">
                      <Badge variant={p.status === "signed" ? "default" : p.status === "rejected" ? "destructive" : "secondary"}>
                        {p.status === "pending" ? "Beklemede" : p.status === "signed" ? "İmzalandı" : "Reddedildi"}
                      </Badge>
                      {p.signed_at && <div className="text-xs text-muted-foreground mt-1">İmza: {new Date(p.signed_at).toLocaleString("tr-TR")}</div>}
                      {p.reject_reason && <div className="text-xs text-destructive mt-1">Ret: {p.reject_reason}</div>}
                      {/* Sadece HR/Admin/GM için 'görüldü' rozeti — personelin bordroyu ilk açtığı an */}
                      {["admin", "hr", "gm"].includes(user?.role) && p.first_viewed_at && (
                        <div
                          className="text-[11px] text-emerald-700 dark:text-emerald-400 mt-1 flex items-center gap-1"
                          title={`Personel tarafından görüntülendi · ${p.first_viewed_device || ""}`}
                          data-testid={`pay-seen-${p.id}`}
                        >
                          <Eye size={12} weight="fill" /> Görüldü — {new Date(p.first_viewed_at).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                          {p.view_count > 1 && <span className="text-muted-foreground">({p.view_count}×)</span>}
                        </div>
                      )}
                      {["admin", "hr", "gm"].includes(user?.role) && !p.first_viewed_at && p.status === "pending" && (
                        <div className="text-[11px] text-amber-600 dark:text-amber-400 mt-1 flex items-center gap-1" data-testid={`pay-unseen-${p.id}`}>
                          <EyeSlash size={12} /> Henüz görüntülenmedi
                        </div>
                      )}
                    </td>
                    <td className="p-3">
                      <div className="flex gap-1 flex-wrap">
                        <a
                          href={`${API}${p.file_url}`}
                          target="_blank"
                          rel="noreferrer"
                          onClick={() => {
                            // Personel bordroyu açtığında 'görüldü' işaretle
                            if (p.user_id === user?.id) {
                              api.post(`/payroll/${p.id}/view`).catch(() => {});
                            }
                          }}
                        >
                          <Button size="sm" variant="ghost" data-testid={`pay-view-${p.id}`}><Download size={14} className="mr-1" />PDF</Button>
                        </a>
                        {p.signed_file_url && (
                          <a href={`${API}${p.signed_file_url}`} target="_blank" rel="noreferrer" title="İmzalı sürüm">
                            <Button size="sm" variant="outline" data-testid={`pay-view-signed-${p.id}`}><Download size={14} className="mr-1" />İmzalı PDF</Button>
                          </a>
                        )}
                        {p.user_id === user?.id && p.status === "pending" && (
                          <>
                            <Button size="sm" onClick={() => setSignOpen(p)} data-testid={`pay-sign-${p.id}`}>Onayla</Button>
                            <Button size="sm" variant="outline" onClick={() => setRejectOpen(p)} data-testid={`pay-reject-${p.id}`}>Reddet</Button>
                          </>
                        )}
                        {p.user_id === user?.id && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-violet-400/50 text-violet-600 dark:text-violet-300 hover:bg-violet-500/10"
                            onClick={() => doInterpret(p)}
                            data-testid={`pay-interpret-${p.id}`}
                          >
                            <Sparkle size={14} weight="fill" className="mr-1" />Yorumla
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {items.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">Kayıt yok</td></tr>}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!signOpen} onOpenChange={(o) => !o && setSignOpen(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Bordro Onayı</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Kayıtlı imzanız otomatik kullanılacaktır. Onaylamak için butona tıklayın.</p>
          <SignatureCapture onChange={setSig} testId="pay-signature" />
          <Button onClick={doSign} disabled={!user?.signature && !sig} data-testid="pay-sign-submit">İmzala ve Onayla</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!rejectOpen} onOpenChange={(o) => !o && setRejectOpen(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Bordro Reddi</DialogTitle></DialogHeader>
          <Label>Hata / eksik açıklaması</Label>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} data-testid="pay-reject-reason" />
          <Button variant="destructive" onClick={doReject} data-testid="pay-reject-submit">Reddet</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!interpretOpen} onOpenChange={(o) => !o && setInterpretOpen(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="pay-interpret-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkle size={18} weight="fill" className="text-violet-500" />
              Ücret Hesap Pusulası Yorumu
            </DialogTitle>
          </DialogHeader>
          {interpretOpen && (
            <div className="text-xs text-muted-foreground -mt-2">
              {interpretOpen.user_name} · {interpretOpen.period}
            </div>
          )}
          {interpretLoading ? (
            <div className="flex flex-col items-center justify-center py-12 gap-3" data-testid="pay-interpret-loading">
              <Sparkle size={28} weight="fill" className="text-violet-500 animate-pulse" />
              <p className="text-sm text-muted-foreground">Yapay zeka pusulanızı yorumluyor…</p>
            </div>
          ) : (
            <div className="max-w-none" data-testid="pay-interpret-content">
              <ReactMarkdown
                components={{
                  h2: ({ node, ...props }) => <h2 className="text-base font-semibold text-violet-600 dark:text-violet-300 mt-4 mb-1.5" {...props} />,
                  h1: ({ node, ...props }) => <h2 className="text-base font-semibold text-violet-600 dark:text-violet-300 mt-4 mb-1.5" {...props} />,
                  h3: ({ node, ...props }) => <h3 className="text-sm font-semibold mt-3 mb-1" {...props} />,
                  p: ({ node, ...props }) => <p className="text-sm text-foreground/90 my-1.5 leading-relaxed" {...props} />,
                  ul: ({ node, ...props }) => <ul className="list-disc pl-5 space-y-1 my-1.5" {...props} />,
                  ol: ({ node, ...props }) => <ol className="list-decimal pl-5 space-y-1 my-1.5" {...props} />,
                  li: ({ node, ...props }) => <li className="text-sm text-foreground/90 leading-relaxed" {...props} />,
                  strong: ({ node, ...props }) => <strong className="font-semibold text-foreground" {...props} />,
                }}
              >
                {interpretText}
              </ReactMarkdown>
            </div>
          )}
          <p className="text-[11px] text-muted-foreground border-t border-border pt-2 mt-2">
            Bu yorum yapay zeka tarafından pusuladaki verilere göre oluşturulmuştur, yalnızca bilgilendirme amaçlıdır. Resmi tutar için pusulanızı esas alın.
          </p>
        </DialogContent>
      </Dialog>

      <Dialog open={!!feedbackOpen} onOpenChange={(o) => !o && skipFeedback()}>
        <DialogContent>
          <DialogHeader><DialogTitle>Kısa bir değerlendirme</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Bordro sürecinden ne kadar memnun kaldınız? (İK ekibimiz için, isteğe bağlıdır.)
          </p>
          <div className="flex gap-2 justify-center py-4" data-testid="pay-feedback-rating">
            {[1,2,3,4,5].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setRating(n)}
                className={`p-2 rounded-full transition-colors ${rating >= n ? "text-warning" : "text-muted-foreground/40 hover:text-warning/60"}`}
                data-testid={`pay-feedback-star-${n}`}
                aria-label={`${n} yıldız`}
              >
                <Star size={32} weight={rating >= n ? "fill" : "regular"} />
              </button>
            ))}
          </div>
          <div>
            <Label>Yorumunuz (opsiyonel)</Label>
            <Textarea value={ratingComment} onChange={(e) => setRatingComment(e.target.value)} placeholder="Süreçle ilgili paylaşmak istediğiniz bir şey…" data-testid="pay-feedback-comment" maxLength={500} />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={skipFeedback} data-testid="pay-feedback-skip">Şimdi değil</Button>
            <Button onClick={submitFeedback} disabled={!rating} data-testid="pay-feedback-submit">Gönder</Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!forceUploadConfirm} onOpenChange={(o) => !o && setForceUploadConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>PDF içeriği eşleşmiyor</AlertDialogTitle>
            <AlertDialogDescription>
              {forceUploadConfirm?.detail}
              <div className="mt-2 text-xs text-muted-foreground">
                Yanlış personele bordro atanmasını önlemek için sistem PDF&apos;in içeriğinde seçili personelin adı veya TC&apos;sini arıyor. Eşleşme bulunamadı. Yine de yükleyecek olursanız bordro seçili personele atanacak.
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="force-upload-cancel">Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={() => doUpload(true)} className="bg-warning text-warning-foreground hover:bg-warning/90" data-testid="force-upload-confirm">
              Yine de Yükle
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
